class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', () => {
      this.toggleResetButton();
      if (this.isZaoudModal() && !this.getQuery().length) {
        this.restoreDefaultCollectionView();
      }
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetClick.bind(this));
    }

    this.toggleResetButton();
  }

  onResetClick(event) {
    if (!this.isZaoudModal()) return;

    event.preventDefault();
    this.input.value = '';
    this.toggleResetButton();

    if (this.hasDefaultCollection()) {
      this.restoreDefaultCollectionView();
      return;
    }

    this.closeResults(true);
  }

  restoreDefaultCollectionView() {
    if (!this.isZaoudModal() || !this.hasDefaultCollection()) return;

    this.searchTerm = this.getQuery();
    this.abortController.abort();
    this.abortController = new AbortController();
    this.clearSelection();
    this.removeAttribute('loading');
    this.toggleResetButton();
    this.showDefaultCollection(true);
  }

  isZaoudModal() {
    return this.dataset.zaoudSearch === 'true';
  }

  closeHeaderSearchModal() {
    document.querySelectorAll('details-modal.header__search').forEach((modal) => {
      if (typeof modal.isOpen === 'function' && modal.isOpen()) {
        modal.close(false);
      }
    });
  }

  getPredictiveSectionId() {
    if (this.isZaoudModal()) {
      return this.dataset.sectionId || 'zaoud-predictive-search';
    }

    return 'predictive-search';
  }

  getResultsCacheKey(searchTerm) {
    return `${this.getPredictiveSectionId()}::${searchTerm.replace(' ', '-').toLowerCase()}`;
  }

  hasDefaultCollection() {
    return Boolean(this.dataset.defaultCollection);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    this.toggleResetButton();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      if (this.isZaoudModal()) {
        this.querySelector('[data-zaoud-search-default]')?.remove();
      }
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      if (this.isZaoudModal() && this.hasDefaultCollection()) {
        this.restoreDefaultCollectionView();
        return;
      }
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();

      if (this.isZaoudModal() && this.hasDefaultCollection()) {
        this.restoreDefaultCollectionView();
        return;
      }

      this.closeResults(true);
    }
  }

  onFocus() {
    if (!this.isZaoudModal()) {
      this.closeHeaderSearchModal();
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.isZaoudModal() && this.hasDefaultCollection() && this.getAttribute('results') !== 'true') {
        this.showDefaultCollection();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.isZaoudModal()) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this.isZaoudModal() && this.hasDefaultCollection()) {
        this.restoreDefaultCollectionView();
      } else {
        this.close(true);
      }
    }

    if (event.code !== 'ArrowUp' && event.code !== 'ArrowDown' && event.code !== 'Enter') {
      return;
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText && previousTerm) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null && !element.hidden
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  handleModalOpen() {
    if (!this.isZaoudModal()) return;

    const query = this.getQuery();
    if (query.length) {
      this.searchTerm = query;
      this.getSearchResults(query);
      return;
    }

    if (this.hasDefaultCollection()) {
      this.showDefaultCollection();
    }
  }

  showDefaultCollection(force = false) {
    if (!this.isZaoudModal() || !this.hasDefaultCollection()) return;

    if (this.getQuery().length) {
      this.searchTerm = this.getQuery();
      this.getSearchResults(this.searchTerm);
      return;
    }

    const handle = this.dataset.defaultCollection;
    const sectionId = this.dataset.defaultSectionId || 'zaoud-search-default';
    const cacheKey = `default-${handle}`;
    const hasDefaultMarkup = Boolean(this.querySelector('[data-zaoud-search-default]'));

    if (
      !force &&
      this.getAttribute('data-zaoud-mode') === 'default' &&
      this.getAttribute('open') &&
      !this.getQuery().length &&
      hasDefaultMarkup
    ) {
      this.toggleResetButton();
      return;
    }

    if (this.cachedResults[cacheKey]) {
      this.renderDefaultCollection(this.cachedResults[cacheKey]);
      return;
    }

    this.setLiveRegionLoadingState();

    fetch(`/collections/${handle}?section_id=${sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const sectionMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${sectionId}`);

        if (!sectionMarkup) return;

        const resultsMarkup = sectionMarkup.innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          if (instance.dataset.defaultCollection === handle) {
            instance.cachedResults[cacheKey] = resultsMarkup;
          }
        });
        this.renderDefaultCollection(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.removeAttribute('loading');
      });
  }

  renderDefaultCollection(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('data-zaoud-mode', 'default');
    this.applyDefaultCollectionSettings();
    this.toggleResetButton();
    this.removeAttribute('loading');
    this.open();
  }

  applyDefaultCollectionSettings() {
    if (!this.isZaoudModal()) return;

    const productsPerRow = Math.min(5, Math.max(3, parseInt(this.dataset.defaultProductsPerRow, 10) || 5));
    const rowCount = 4;
    const visibleCount = productsPerRow * rowCount;
    const grid = this.querySelector('.zaoud-search-default__grid');
    const items = this.querySelectorAll('[data-zaoud-default-product]');

    if (grid) {
      grid.style.setProperty('--zaoud-search-default-cols', productsPerRow);
      grid.style.setProperty('--zaoud-search-default-rows', rowCount);
    }

    items.forEach((item, index) => {
      item.hidden = index >= visibleCount;
    });

    const customHeading = this.dataset.defaultHeading?.trim();
    const title = this.querySelector('.zaoud-search-default__title');
    if (title && customHeading) {
      title.textContent = customHeading;
    }
  }

  getSearchResults(searchTerm) {
    const sectionId = this.getPredictiveSectionId();
    const queryKey = this.getResultsCacheKey(searchTerm);
    const requestedTerm = searchTerm;
    this.setLiveRegionLoadingState();
    if (this.isZaoudModal()) {
      this.setAttribute('data-zaoud-mode', 'predictive');
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    const params = new URLSearchParams({
      q: searchTerm,
      section_id: sectionId,
    });

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.getQuery() !== requestedTerm) {
          this.removeAttribute('loading');
          return;
        }

        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${sectionId}`).innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          if (predictiveSearchInstance.getPredictiveSectionId() === sectionId) {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.isZaoudModal() && !this.getQuery().length) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    if (this.isZaoudModal()) {
      this.setAttribute('data-zaoud-mode', 'predictive');
      this.applyZaoudSearchSettings();
    } else {
      this.removeAttribute('data-zaoud-mode');
    }

    this.setLiveRegionResults();
    this.open();
  }

  applyZaoudSearchSettings() {
    if (!this.isZaoudModal()) return;

    const productsPerRow = Math.min(5, Math.max(3, parseInt(this.dataset.productsPerRow, 10) || 3));
    const collectionsHeading = this.dataset.collectionsHeading || 'Collections';
    const layout = this.querySelector('.zaoud-predictive-search__layout');
    const heading = this.querySelector('#zaoud-predictive-search-collections');
    const productItems = this.querySelectorAll('.zaoud-predictive-search__product-item');

    if (layout) {
      layout.style.setProperty('--zaoud-search-products-cols', productsPerRow);
    }

    if (heading) {
      heading.textContent = collectionsHeading;
    }

    productItems.forEach((item) => {
      item.hidden = false;
    });

    const totalCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (totalCount) {
      const visibleProducts = productItems.length;
      const collectionCount = this.querySelectorAll('.zaoud-predictive-search__collection-item').length;
      const total = collectionCount + visibleProducts;
      totalCount.dataset.totalResults = total;
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      this.setLiveRegionText(liveRegion.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.isZaoudModal()) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    } else {
      this.predictiveSearchResults.style.maxHeight = '';
      this.predictiveSearchResults.style.overflowY = '';
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  clearSelection() {
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.removeAttribute('data-zaoud-mode');
    }
    this.clearSelection();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
